import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, merge, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { AuthService } from './auth.service';

@Injectable({
    providedIn: 'root'
})
export class IdleTimeoutService implements OnDestroy {
    private readonly inactivityTimeoutMs = 60 * 60 * 1000;
    private activitySubscription: Subscription;
    private logoutTimer: number = null;
    private isWatching = false;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private ngZone: NgZone,
        private authService: AuthService
    ) { }

    startWatching(): void {
        if (this.isWatching) {
            this.refreshTimerFromAuthState();
            return;
        }

        this.isWatching = true;

        this.ngZone.runOutsideAngular(() => {
            this.activitySubscription = merge(
                fromEvent(this.document, 'mousemove'),
                fromEvent(this.document, 'keydown'),
                fromEvent(this.document, 'click'),
                fromEvent(this.document, 'scroll'),
                fromEvent(this.document, 'touchstart')
            )
                .pipe(throttleTime(1000))
                .subscribe(() => this.handleActivity());
        });

        this.refreshTimerFromAuthState();
    }

    stopWatching(): void {
        this.clearLogoutTimer();
        if (this.activitySubscription) {
            this.activitySubscription.unsubscribe();
            this.activitySubscription = null;
        }
        this.isWatching = false;
    }

    refreshTimerFromAuthState(): void {
        if (localStorage.getItem('auth_token')) {
            this.resetLogoutTimer();
        } else {
            this.clearLogoutTimer();
        }
    }

    ngOnDestroy(): void {
        this.stopWatching();
    }

    private handleActivity(): void {
        if (!localStorage.getItem('auth_token')) {
            this.clearLogoutTimer();
            return;
        }

        this.resetLogoutTimer();
    }

    private resetLogoutTimer(): void {
        this.clearLogoutTimer();
        this.logoutTimer = window.setTimeout(() => {
            this.ngZone.run(() => {
                if (localStorage.getItem('auth_token')) {
                    this.authService.logout(true);
                }
            });
        }, this.inactivityTimeoutMs);
    }

    private clearLogoutTimer(): void {
        if (this.logoutTimer !== null) {
            window.clearTimeout(this.logoutTimer);
            this.logoutTimer = null;
        }
    }
}
